import React from 'react';
import { Loader } from 'lucide-react';
import { CATEGORY_OPTIONS } from '../constants/categories';

type Props = {
  initial?: any;
  onCancel?: () => void;
  onSubmit: (data: any) => Promise<void> | void;
};

export default function TransactionForm({ initial, onCancel, onSubmit }: Props) {
  const [form, setForm] = React.useState(() => ({
    type: initial?.type || 'debit',
    category: initial?.category || CATEGORY_OPTIONS[0],
    description: initial?.description || '',
    amount: initial?.amount ?? 0,
    isPaid: initial?.isPaid || false,
    dueDate: initial?.dueDate ? new Date(initial.dueDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
  }));
  const [isLoading, setIsLoading] = React.useState(false);

  const [displayAmount, setDisplayAmount] = React.useState(() => 
    initial?.amount ? new Intl.NumberFormat(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(initial.amount) : ''
  );

  const handleChange = (key: string, value: any) => {
    setForm((s) => ({ ...s, [key]: value }));
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    if (value === '') {
      setDisplayAmount('');
      handleChange('amount', 0);
      return;
    }
    const numericValue = parseFloat(value) / 100;
    setDisplayAmount(new Intl.NumberFormat(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numericValue));
    handleChange('amount', numericValue);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    try {
      const dueDate = form.dueDate ? new Date(form.dueDate) : new Date();
      const payload = {
        ...form,
        amount: Number(form.amount),
        dueDate: dueDate.toISOString(),
        month: dueDate.getMonth() + 1,
        year: dueDate.getFullYear(),
      };
      await onSubmit(payload);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Tipo</label>
        <select
          value={form.type}
          onChange={(e) => handleChange('type', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none text-gray-900 bg-white"
        >
          <option value="debit">Despesa</option>
          <option value="credit">Receita</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Categoria</label>
        <select
          value={form.category}
          onChange={(e) => handleChange('category', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none text-gray-900 bg-white"
        >
          {CATEGORY_OPTIONS.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Descrição</label>
        <input
          placeholder="Ex: Padaria"
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Valor</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
            {new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'BRL' }).format(0).replace(/[\d.,\s]/g, '')}
          </span>
          <input
            type="text"
            placeholder="0,00"
            value={displayAmount}
            onChange={handleAmountChange}
            className="w-full border border-gray-300 pl-10 pr-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Data de Vencimento</label>
        <input
          type="date"
          value={form.dueDate}
          onChange={(e) => handleChange('dueDate', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none text-gray-900 bg-white"
        />
      </div>

      <div className="flex items-center">
        <input
          id="isPaid"
          type="checkbox"
          checked={form.isPaid}
          onChange={(e) => handleChange('isPaid', e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
        />
        <label htmlFor="isPaid" className="ml-2 text-sm font-medium text-gray-900">
          Pago
        </label>
      </div>

      <div className="flex gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-md font-medium hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading && <Loader className="w-4 h-4 animate-spin" />}
          {isLoading ? 'Processando...' : (initial ? 'Atualizar' : 'Criar')}
        </button>
      </div>
    </form>
  );
}
